import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatSessionDateTime } from '../utils/sessionDateTime';

export function findAutoCompactMarkerForMessage(compactSummaries, message, nextMessage) {
  if (!Array.isArray(compactSummaries) || compactSummaries.length === 0) return null;
  const prevId = Number(message?.id);
  const nextId = nextMessage ? Number(nextMessage.id) : Number.POSITIVE_INFINITY;
  return compactSummaries.find((s) => {
    const cutoff = Number(s?.cutoff_message_id);
    return (
      (Number.isFinite(cutoff) && cutoff === prevId) ||
      (Number.isFinite(cutoff) && cutoff > prevId && cutoff < nextId)
    );
  }) || null;
}

function getSummaryBody(summary) {
  const text = summary?.summary;
  return typeof text === 'string' ? text.trim() : '';
}

const AutoCompactMarker = ({ summary }) => {
  if (!summary) return null;

  const coveredCount = summary.cutoff_message_count ?? 0;
  const coveredChars = summary.total_chars_summarized ?? 0;
  const elapsedMs = summary.elapsed_ms ?? 0;
  const model = summary.model || 'unknown';
  const createdAt = summary.created_at ? formatSessionDateTime(summary.created_at) : '';
  const summaryBody = getSummaryBody(summary);
  const metaLine = `Covered ${coveredCount} msg / ${coveredChars} chars · ${model} · ${elapsedMs}ms${createdAt ? ` · ${createdAt}` : ''}`;

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        my: 1,
        borderRadius: 1,
        border: '1px dashed',
        borderColor: 'warning.main',
        bgcolor: 'rgba(237, 108, 2, 0.08)',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: 'warning.dark' }} />}
        aria-controls={`autocompact-${summary.id || summary.cutoff_message_id}-content`}
        id={`autocompact-${summary.id || summary.cutoff_message_id}-header`}
        sx={{
          minHeight: 48,
          px: 1.25,
          '& .MuiAccordionSummary-content': { my: 0.75 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'warning.dark',
              fontWeight: 700,
              fontSize: { xs: '0.7rem', md: '0.8rem' },
            }}
          >
            Auto-Compact · {coveredCount} msgs summarized
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {metaLine}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0, px: 1.25, pb: 1.25 }}>
        {summaryBody ? (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.25,
              borderRadius: 1,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              fontFamily: 'inherit',
              fontSize: { xs: '0.75rem', md: '0.8125rem' },
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: 'text.primary',
              maxHeight: 320,
              overflowY: 'auto',
            }}
          >
            {summaryBody}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Summary text is not available for this marker yet.
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AutoCompactMarker;
